'use client'

import { isBefore } from 'date-fns/isBefore'
import { isAfter } from 'date-fns/isAfter'
import { useMemo, useState } from 'react'
import { useInterval } from 'usehooks-ts'
import Modal from '~/components/Modal'
import useModalState from '~/hooks/useModalState'
import { getEventInfo } from '~/lib/aion-schedule'
import { cn } from '~/utils/cn'
import GameEvent from './game-event'

const computeEvents = (eventData: {
	title: string
	image: string | null
	openStateVerb: string
	closeStateVerb: string
	timezoneOverride?: string | null
	occurrences: ({
		_type: 'cadencedOccurrence'
		days: {
			sunday: boolean
			monday: boolean
			tuesday: boolean
			wednesday: boolean
			thursday: boolean
			friday: boolean
			saturday: boolean
		}
		start: {
			hour: number
			minute: number
			second: number
		}
		end: {
			hour: number
			minute: number
			second: number
		}
	} | {
		_type: 'datedOccurrence'
		startDate: Date
		endDate: Date
	})[]
}[], timezone: string) => {
	return eventData.map((event) => {
		return {
			data: event,
			info: getEventInfo({
				timezoneOverride: event.timezoneOverride,
				occurrences: event.occurrences,
			}, timezone),
		}
	})
}

const formatDateRow = (start: Date, end: Date, timezone: string) => {
	const dayFormatter = new Intl.DateTimeFormat(undefined, {
		weekday: 'short',
		timeZone: timezone,
	})
	const dateFormatter = new Intl.DateTimeFormat(undefined, {
		month: 'short',
		day: 'numeric',
		timeZone: timezone,
	})
	const timeFormatter = new Intl.DateTimeFormat(undefined, {
		hour: 'numeric',
		minute: '2-digit',
		timeZone: timezone,
	})

	return {
		day: dayFormatter.format(start),
		date: dateFormatter.format(start),
		startTime: timeFormatter.format(start),
		endTime: timeFormatter.format(end),
	}
}

const SchedulePanel = ({
	timezone,
	eventData,
}: {
	timezone: string
	eventData: Parameters<typeof computeEvents>[0]
}) => {
	const initialEvents = useMemo(() => {
		return computeEvents(eventData, timezone)
	}, [
		eventData,
		timezone,
	])

	const [
		events,
		setEvents,
	] = useState(initialEvents)

	const [
		selectedIndex,
		setSelectedIndex,
	] = useState<number | null>(null)

	const [
		open,
		setOpen,
	] = useModalState()

	useInterval(() => {
		setEvents(computeEvents(eventData, timezone))
	}, 1000)

	const sortedEvents = events.toSorted((a, b) => {
		if (a.info.nextEvent === null) {
			return -1
		}

		if (b.info.nextEvent === null) {
			return 1
		}

		return isBefore(a.info.nextEvent, b.info.nextEvent) ? -1 : 1
	})

	const selectedEvent = selectedIndex !== null ? sortedEvents[selectedIndex] : null

	return (
		<>
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
				{sortedEvents.map((event, index) => {
					return (
						<GameEvent
							key={event.data.title}
							title={event.data.title}
							countdown={event.info.state.countdown}
							humanReadableCountdown={event.info.state.humanReadableCountdown}
							state={event.info.state.currentState}
							image={event.data.image}
							openStateVerb={event.data.openStateVerb}
							closeStateVerb={event.data.closeStateVerb}
							onClick={() => {
								setSelectedIndex(index)
								setOpen(true)
							}}
						/>
					)
				})}
			</div>
			<Modal
				name="event-schedule"
				open={open}
				setOpen={setOpen}
			>
				{selectedEvent ? (
					<div className="min-w-80 max-w-lg">
						<div className="flex items-center justify-between gap-4 mb-4">
							<h2 className="font-bold text-2xl text-white">
								{selectedEvent.data.title}
							</h2>
							<span
								className={cn(
									'shrink-0 rounded-full px-3 py-1 text-sm font-bold text-white',
									selectedEvent.info.isOpen ? 'bg-green-700' : 'bg-red-700',
								)}
							>
								{selectedEvent.info.isOpen
									? selectedEvent.data.openStateVerb
									: selectedEvent.data.closeStateVerb}
							</span>
						</div>
						<p className="text-gray-300 mb-4">
							{`${selectedEvent.info.isOpen ? selectedEvent.data.closeStateVerb : selectedEvent.data.openStateVerb} ${selectedEvent.info.state.humanReadableCountdown}`}
						</p>
						<p className="text-white text-3xl font-black tabular-nums mb-6">
							{selectedEvent.info.state.countdown}
						</p>
						<h3 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-2">
							Upcoming Schedule
						</h3>
						<ul className="space-y-1">
							{selectedEvent.info.dates.map((date, i) => {
								const now = new Date()
								const isActive = isAfter(now, date.start) && isBefore(now, date.end)
								const formatted = formatDateRow(date.start, date.end, selectedEvent.data.timezoneOverride ?? timezone)

								return (
									<li
										key={i}
										className={cn(
											'flex items-center gap-3 rounded-lg px-3 py-2 text-sm',
											isActive
												? 'bg-green-900/50 text-green-300'
												: 'text-gray-300',
										)}
									>
										<span className="w-10 font-bold">
											{formatted.day}
										</span>
										<span className="w-16">
											{formatted.date}
										</span>
										<span className="ml-auto tabular-nums">
											{`${formatted.startTime} — ${formatted.endTime}`}
										</span>
									</li>
								)
							})}
						</ul>
						<button
							type="button"
							onClick={() => {
								setOpen(false)
							}}
							className="mt-6 w-full rounded-lg bg-gray-700 px-4 py-2 text-sm font-bold text-white hover:bg-gray-600 transition-colors cursor-pointer"
						>
							Close
						</button>
					</div>
				) : null}
			</Modal>
		</>
	)
}

export default SchedulePanel
